import { Routes } from 'common/routeConfig'
import StaticPageWrapper from 'components/staticPage/StaticPageWrapper'
import { useDataContext } from 'context/DataContext'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useParseHTML } from 'utils/hooks/use-parse-html'
import disclaimerUrl from 'assets/static/disclaimer.html'
import disclaimerUrlHu from 'assets/static/disclaimer.hu.html'
import { DEFAULT_LANGUAGE, LanguageCode } from 'translations'

type StaticPageProps = {
    page: keyof typeof staticPageUrls
    withTitle?: boolean
}

const staticPageUrls = {
    disclaimer: {
        en: disclaimerUrl,
        hu: disclaimerUrlHu
    }
} as const satisfies Record<string, Partial<Record<LanguageCode, string>>>

const englishMonthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
]

const formatDatabaseDate = (
    databaseDate: string | undefined,
    language: string
) => {
    if (!databaseDate) {
        return ''
    }

    const datePart = databaseDate.split(' ')[0]
    const [year, month, day] = datePart.split('-')

    const monthIndex = Number(month) - 1
    const dayNumber = Number(day)

    if (!year || monthIndex < 0 || monthIndex > 11 || !dayNumber) {
        return databaseDate
    }

    if (language === DEFAULT_LANGUAGE) {
        return `${englishMonthNames[monthIndex]} ${dayNumber} ${year}`
    }

    return new Intl.DateTimeFormat(language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).format(new Date(Number(year), monthIndex, dayNumber))
}

const StaticPage: React.FC<StaticPageProps> = ({ page, withTitle }) => {
    const [htmlContent, setHtmlContent] = useState<string | null>(null)
    const navigate = useNavigate()
    const { t, i18n } = useTranslation('pages')
    const { metaInfo } = useDataContext()

    const language = i18n.resolvedLanguage ?? DEFAULT_LANGUAGE
    const documentUrls = staticPageUrls[page]
    const documentUrl =
        documentUrls[language as LanguageCode] ?? documentUrls[DEFAULT_LANGUAGE]

    const formattedDatabaseDate = formatDatabaseDate(
        metaInfo?.databaseDate,
        language
    )

    useEffect(() => {
        async function fetchHtmlContent() {
            try {
                const response = await fetch(documentUrl)

                if (!response.ok) {
                    navigate(Routes.NOT_FOUND)
                    return
                }

                let html = await response.text()

                if (page === 'disclaimer') {
                    html = html.replace(
                        '{{databaseDate}}',
                        formattedDatabaseDate || 'N/A'
                    )
                }

                setHtmlContent(html)
            } catch (error) {
                navigate(Routes.NOT_FOUND)
                console.error('Error fetching HTML content:', error)
            }
        }

        fetchHtmlContent()
    }, [navigate, page, documentUrl, formattedDatabaseDate])

    const parsedHTML = useParseHTML(htmlContent)

    return (
        <StaticPageWrapper
            isContentPage
            title={withTitle ? t(`${page}.title`) : undefined}
        >
            {parsedHTML}
        </StaticPageWrapper>
    )
}

export default StaticPage